/**
 * KK-502 / KK-503 / KK-505 · Ürün kartı, çoklu barkod, toplu fiyat, fiyat geçmişi.
 *
 * KK-506 gereği fiyat değişikliği GEÇMİŞ SATIŞLARI ETKİLEMEZ: satış satırı o
 * anki fiyatı kendi üzerinde taşır. Bu yüzden "fiyat geçmişi" bir hesap kaynağı
 * değil, bir DENETİM kaynağıdır ve audit log'dan okunur.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
  hidOkuyucuDinle,
} from "@kantin/ui";
import { HataKodu } from "@kantin/shared";
import { ApiHatasi, apiOku, apiYaz } from "./api.js";
import { urunBarkodlariniHazirla, urunBarkodSahibiniBul } from "./urunBarkodlari.js";
import { useKategoriler } from "./kancalar.js";
import type { Urun } from "./kancalar.js";
import { UrunDurumOnayi } from "./UrunDurumOnayi.js";
import {
  AramaKutusu,
  IslemDurumu,
  MetinAlani,
  OnayKutusu,
  Sayfa,
  SayfaBasligi,
  Sekmeler,
  SorguDurumu,
  miktarGecerliMi,
  miktarNormalize,
  paraGecerliMi,
  paraNormalize,
  tarihSaatGoster,
} from "./ortak.js";

type Sekme = "liste" | "toplu-fiyat" | "gecmis";

const LISTE_LIMITI = 100;

export function Urunler() {
  const [sekme, setSekme] = useState<Sekme>("liste");
  const [secilenler, setSecilenler] = useState<string[]>([]);

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Ürünler"
        aciklama="Ürün fiyatları okulun tüm kantinlerinde aynıdır. Fiyat değişiklikleri geçmiş satışları etkilemez."
      />

      <Sekmeler<Sekme>
        etiket="Ürün bölümleri"
        secili={sekme}
        sec={setSekme}
        sekmeler={[
          { deger: "liste", metin: "Ürün listesi" },
          {
            deger: "toplu-fiyat",
            metin: "Toplu fiyat güncelleme",
            sayi: secilenler.length,
          },
          { deger: "gecmis", metin: "Fiyat geçmişi" },
        ]}
      />

      {sekme === "liste" && (
        <UrunListesi secilenler={secilenler} secimDegistir={setSecilenler} />
      )}
      {sekme === "toplu-fiyat" && (
        <TopluFiyat
          secilenler={secilenler}
          secimTemizle={() => setSecilenler([])}
        />
      )}
      {sekme === "gecmis" && <FiyatGecmisi />}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------
// Liste
// ---------------------------------------------------------------------------

function UrunListesi({
  secilenler,
  secimDegistir,
}: {
  secilenler: string[];
  secimDegistir: (yeni: string[]) => void;
}) {
  const [arama, setArama] = useState("");
  const [kategoriId, setKategoriId] = useState("");
  const [aktif, setAktif] = useState("true");
  const [duzenlenen, setDuzenlenen] = useState<Urun | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);
  const [durumUrunu, setDurumUrunu] = useState<Urun | null>(null);
  const [durumMesaji, setDurumMesaji] = useState("");

  const kategoriler = useKategoriler();

  const sorgu = useQuery({
    queryKey: ["urun", arama, kategoriId, aktif],
    queryFn: () =>
      apiOku<{ urunler: Urun[] }>("/urun", {
        arama: arama || undefined,
        kategori_id: kategoriId || undefined,
        aktif: aktif || undefined,
        limit: LISTE_LIMITI,
      }),
  });

  const urunler = sorgu.data?.urunler ?? [];

  return (
    <>
      <Kart baslik="Süzgeçler">
        <div className="grid gap-3 md:grid-cols-3">
          <AramaKutusu
            etiket="Ara"
            ipucu="Ürün adı veya barkod"
            deger={arama}
            degistir={setArama}
          />
          <Secim
            etiket="Kategori"
            value={kategoriId}
            onChange={(e) => setKategoriId(e.target.value)}
            secenekler={[
              { deger: "", metin: "Tüm kategoriler" },
              ...(kategoriler.data?.kategoriler ?? []).map((k) => ({
                deger: k.id,
                metin: k.ad,
              })),
            ]}
          />
          <Secim
            etiket="Durum"
            value={aktif}
            onChange={(e) => setAktif(e.target.value)}
            secenekler={[
              { deger: "true", metin: "Aktif" },
              { deger: "false", metin: "Pasif" },
              { deger: "", metin: "Hepsi" },
            ]}
          />
        </div>
      </Kart>

      <div className="flex flex-wrap items-center gap-2">
        <Buton
          tur="birincil"
          onClick={() => {
            setYeniAcik((a) => !a);
            setDuzenlenen(null);
          }}
          aria-expanded={yeniAcik}
        >
          {yeniAcik ? "Formu kapat" : "Yeni ürün"}
        </Buton>
        {secilenler.length > 0 && (
          <span className="text-sm text-murekkep-2">
            <span className="rakam font-mono">{secilenler.length}</span> ürün
            seçili — &quot;Toplu fiyat güncelleme&quot; sekmesinde
            kullanılabilir.
          </span>
        )}
      </div>

      {yeniAcik && (
        <UrunFormu
          urun={null}
          kapat={() => setYeniAcik(false)}
          kaydedildi={() => {
            setYeniAcik(false);
            void sorgu.refetch();
          }}
          barkodAra={setArama}
        />
      )}

      {duzenlenen && (
        <UrunFormu
          urun={duzenlenen}
          kapat={() => setDuzenlenen(null)}
          kaydedildi={() => {
            setDuzenlenen(null);
            void sorgu.refetch();
          }}
          barkodAra={setArama}
        />
      )}

      {durumMesaji && <p role="status">{durumMesaji}</p>}
      {durumUrunu && (
        <UrunDurumOnayi
          key={durumUrunu.id}
          urun={durumUrunu}
          kapat={() => setDurumUrunu(null)}
          kaydedildi={() => {
            setDurumMesaji(`${durumUrunu.ad} ${durumUrunu.aktif ? "pasife alındı" : "aktifleştirildi"}.`);
            secimDegistir(secilenler.filter((id) => id !== durumUrunu.id));
            setDurumUrunu(null);
          }}
        />
      )}

      <Kart
        baslik="Ürünler"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{urunler.length}</span> kayıt
            {urunler.length === LISTE_LIMITI ? " (ilk 100)" : ""}
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={urunler.length === 0}
          bosBaslik="Ürün bulunamadı"
          bosAciklama="Süzgeçleri genişletin veya yeni ürün ekleyin."
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">
                <span className="gizli-erisilebilir">Seç</span>
              </th>
              <th scope="col">Ürün</th>
              <th scope="col">Barkodlar</th>
              <th scope="col">Alerjenler</th>
              <th scope="col" className="!text-right">
                Satış fiyatı
              </th>
              <th scope="col" className="!text-right">
                Kritik stok
              </th>
              <th scope="col" className="!text-right">
                Hızlı buton
              </th>
              <th scope="col">
                <span className="gizli-erisilebilir">İşlem</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {urunler.map((u) => (
                <tr key={u.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={secilenler.includes(u.id)}
                      onChange={(e) =>
                        secimDegistir(
                          e.target.checked
                            ? [...secilenler, u.id]
                            : secilenler.filter((x) => x !== u.id),
                        )
                      }
                      aria-label={`${u.ad} ürününü toplu fiyat için seç`}
                      className="h-4 w-4 accent-simit"
                    />
                  </td>
                  <td>
                    <p className="m-0 font-semibold text-murekkep">{u.ad}</p>
                    <p className="m-0 text-xs text-murekkep-3">
                      {u.kategori_ad ?? "Kategorisiz"} · {u.birim}
                      {u.skt_takibi ? " · SKT takipli" : ""}
                      {u.ozel_siparis ? " · Özel sipariş" : ""}
                      {!u.aktif ? " · pasif" : ""}
                      {u.aktif && !u.satilabilir
                        ? " · bu noktada satışta değil"
                        : ""}
                    </p>
                  </td>
                  <td className="rakam font-mono text-xs">
                    {u.barkodlar.length === 0 ? (
                      <span className="text-murekkep-3">—</span>
                    ) : (
                      u.barkodlar.map((b) => <div key={b}>{b}</div>)
                    )}
                  </td>
                  <td>
                    {u.alerjenler.length === 0 ? (
                      <span className="text-xs text-murekkep-3">—</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {u.alerjenler.map((a) => (
                          <Rozet key={a} tur="uyari">
                            {a}
                          </Rozet>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    <Tutar deger={u.satis_fiyat} yonsuz etiket="satış fiyatı" />
                  </td>
                  <td className="rakam text-right font-mono text-xs">
                    {u.kritik_seviye ?? "—"}
                  </td>
                  <td className="rakam text-right font-mono text-xs">
                    {u.hizli_buton_sira ?? "—"}
                  </td>
                  <td className="text-right">
                    <Buton
                      tur="sessiz"
                      onClick={() => {
                        setDuzenlenen(u);
                        setYeniAcik(false);
                      }}
                    >
                      Düzenle
                    </Buton>
                    <Buton
                      tur={u.aktif ? "tehlike" : "onay"}
                      onClick={() => {
                        setDurumUrunu(u);
                        setDurumMesaji("");
                        setDuzenlenen(null);
                        setYeniAcik(false);
                      }}
                    >
                      {u.aktif ? "Pasife al" : "Aktifleştir"}
                    </Buton>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>
    </>
  );
}

// ---------------------------------------------------------------------------
// Etiket düzenleyici (barkod ve alerjen)
// ---------------------------------------------------------------------------

function EtiketDuzenleyici({
  etiket,
  ipucu,
  degerler,
  degistir,
  tekil,
  taslak: disTaslak,
  taslakDegistir,
  hata,
  autoFocus = false,
}: {
  etiket: string;
  ipucu?: string;
  degerler: string[];
  degistir: (yeni: string[]) => void;
  tekil: string;
  taslak?: string;
  taslakDegistir?: (yeni: string) => void;
  hata?: string;
  autoFocus?: boolean;
}) {
  const [yerelTaslak, setYerelTaslak] = useState("");
  const taslak = disTaslak ?? yerelTaslak;
  const setTaslak = taslakDegistir ?? setYerelTaslak;
  const ekle = () => {
    if (hata) return;
    const d = taslak.trim();
    if (!d || degerler.includes(d)) {
      setTaslak("");
      return;
    }
    degistir([...degerler, d]);
    setTaslak("");
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Alan
            etiket={etiket}
            autoFocus={autoFocus}
            value={taslak}
            onChange={(e) => setTaslak(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                ekle();
              }
            }}
            {...(ipucu ? { ipucu } : {})}
            {...(hata ? { hata } : {})}
          />
        </div>
        <Buton type="button" tur="ikincil" onClick={ekle}>
          Ekle
        </Buton>
      </div>
      {degerler.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {degerler.map((d) => (
            <li key={d}>
              <button
                type="button"
                onClick={() => degistir(degerler.filter((x) => x !== d))}
                aria-label={`${d} ${tekil} kaydını kaldır`}
                className="inline-flex min-h-hedef items-center gap-2 rounded-md border border-cetvel bg-kagit-2 px-3 text-sm text-murekkep transition-colors duration-defter ease-orta hover:border-cetvel-koyu hover:bg-kagit-3"
              >
                <span className="rakam font-mono">{d}</span>
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KK-503 · Barkod çakışması
// ---------------------------------------------------------------------------

interface BarkodSahibi {
  barkod: string;
  urun_id: string;
  urun_ad: string;
}

/**
 * Barkod okul içinde tekildir. Kullanıcı KAYDETMEDEN ÖNCE çakışmayı görsün
 * diye barkodlar eklendiği anda sahibi sorulur: sunucunun 409'unu beklemek
 * "neden kaydedilmedi" sorusunu formun en altına iter.
 *
 * Listedeki barkodlar hemen, henüz eklenmemiş taslak yazım durunca sorgulanır.
 */
function useBarkodSahipleri(
  barkodlar: string[],
  urunId: string | null,
  taslak: string,
) {
  const [bekleyenTaslak, setBekleyenTaslak] = useState("");
  const temizTaslak = taslak.trim();
  useEffect(() => {
    const zamanlayici = window.setTimeout(() => setBekleyenTaslak(temizTaslak), 300);
    return () => window.clearTimeout(zamanlayici);
  }, [temizTaslak]);
  const kontrolEdilecekler = [...new Set([
    ...barkodlar,
    ...(bekleyenTaslak === temizTaslak ? [temizTaslak] : []),
  ])].filter((b) => b.length > 0 && b.length <= 64);
  const anahtar = [...kontrolEdilecekler].sort().join("\u0000");
  const sorgu = useQuery({
    queryKey: ["barkod-sahibi", anahtar, urunId],
    enabled: kontrolEdilecekler.length > 0,
    queryFn: async (): Promise<BarkodSahibi[]> => {
      const sonuclar = await Promise.all(
        kontrolEdilecekler.map((b) =>
          urunBarkodSahibiniBul(b, urunId).then(
            (sahip) => ({ barkod: b, sahip }),
          ),
        ),
      );
      const sahipler: BarkodSahibi[] = [];
      for (const { barkod, sahip } of sonuclar) {
        if (sahip) sahipler.push({ barkod, urun_id: sahip.id, urun_ad: sahip.ad });
      }
      return sahipler;
    },
  });
  return sorgu;
}

function BarkodCakismaUyarisi({
  sahip,
  devredilecek,
  devret,
  geriAl,
  barkodAra,
}: {
  sahip: BarkodSahibi;
  devredilecek: boolean;
  devret: () => void;
  geriAl: () => void;
  barkodAra: (barkod: string) => void;
}) {
  return (
    <Uyari
      tur={devredilecek ? "bilgi" : "uyari"}
      baslik={
        devredilecek
          ? "Barkod bu ürüne taşınacak"
          : "Bu barkod başka bir üründe kayıtlı"
      }
    >
      <p className="m-0">
        <span className="rakam font-mono">{sahip.barkod}</span> barkodu{" "}
        <strong>{sahip.urun_ad}</strong> ürününde kayıtlı.{" "}
        {devredilecek
          ? "Kaydedince barkod bu üründen okunacak, eski ürün barkodsuz kalacak."
          : "Aynı barkod iki üründe olamaz — kasada hangi ürünün satıldığı belirsizleşir."}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Buton type="button" tur="sessiz" onClick={() => barkodAra(sahip.barkod)}>
          Listede göster
        </Buton>
        {devredilecek ? (
          <Buton type="button" tur="sessiz" onClick={geriAl}>
            Taşımaktan vazgeç
          </Buton>
        ) : (
          <Buton type="button" tur="ikincil" onClick={devret}>
            Bu ürüne taşı
          </Buton>
        )}
      </div>
    </Uyari>
  );
}

// ---------------------------------------------------------------------------
// Ürün formu
// ---------------------------------------------------------------------------

export function UrunFormu({
  urun,
  kapat,
  kaydedildi,
  barkodAra,
}: {
  urun: Urun | null;
  kapat: () => void;
  kaydedildi: () => void;
  /** Çakışan barkodun sahibini listede göstermek için süzgece yazar. */
  barkodAra: (barkod: string) => void;
}) {
  const kategoriler = useKategoriler();
  const [ad, setAd] = useState(urun?.ad ?? "");
  const [kategoriId, setKategoriId] = useState(urun?.kategori_id ?? "");
  const [satisFiyat, setSatisFiyat] = useState(urun?.satis_fiyat ?? "");
  const [alisFiyat, setAlisFiyat] = useState(urun?.alis_fiyat ?? "0.00");
  const [kdv, setKdv] = useState(urun?.kdv_orani ?? "");
  const [birim, setBirim] = useState(urun?.birim ?? "adet");
  const [kritik, setKritik] = useState(urun?.kritik_seviye ?? "");
  const [alerjenler, setAlerjenler] = useState<string[]>(
    urun?.alerjenler ?? [],
  );
  const [barkodlar, setBarkodlar] = useState<string[]>(urun?.barkodlar ?? []);
  const [barkodTaslagi, setBarkodTaslagi] = useState("");
  // KK-503: başka üründen devralınması AÇIKÇA onaylanan barkodlar. Onay
  // barkodun kendisine bağlıdır; barkod listeden çıkarsa onay da düşer.
  const [devredilecek, setDevredilecek] = useState<string[]>([]);
  const [gorsel, setGorsel] = useState(urun?.gorsel_url ?? "");
  const [hizliSira, setHizliSira] = useState(
    urun?.hizli_buton_sira === null || urun?.hizli_buton_sira === undefined
      ? ""
      : String(urun.hizli_buton_sira),
  );
  const [sktTakibi, setSktTakibi] = useState(urun?.skt_takibi ?? false);
  const [ozelSiparis, setOzelSiparis] = useState(urun?.ozel_siparis ?? false);
  const [rafOmru, setRafOmru] = useState(
    urun?.raf_omru_gun === null || urun?.raf_omru_gun === undefined
      ? ""
      : String(urun.raf_omru_gun),
  );
  const [iadeEsigi, setIadeEsigi] = useState(String(urun?.iade_esigi_gun ?? 1));
  const [gonderildi, setGonderildi] = useState(false);

  const govde = (kaydedilecekBarkodlar: string[]) => ({
    ad: ad.trim(),
    kategori_id: kategoriId || null,
    satis_fiyat: paraNormalize(satisFiyat),
    alis_fiyat: paraNormalize(alisFiyat || "0"),
    kdv_orani: kdv.trim() ? paraNormalize(kdv) : null,
    birim: birim.trim() || "adet",
    kritik_seviye: kritik.trim() ? miktarNormalize(kritik) : null,
    alerjenler,
    barkodlar: kaydedilecekBarkodlar,
    barkod_devral: kaydedilecekBarkodlar.some((b) => devredilecek.includes(b)),
    gorsel_url: gorsel.trim() || null,
    hizli_buton_sira: hizliSira.trim() ? Number.parseInt(hizliSira, 10) : null,
    skt_takibi: sktTakibi,
    ozel_siparis: ozelSiparis,
    raf_omru_gun: rafOmru.trim() ? Number.parseInt(rafOmru, 10) : null,
    iade_esigi_gun: Number.parseInt(iadeEsigi || "1", 10),
  });

  const kaydet = useMutation({
    mutationFn: async () => {
      const kaydedilecekBarkodlar = await urunBarkodlariniHazirla(
        barkodlar,
        barkodTaslagi,
        devredilecek,
        urun?.id ?? null,
      );
      return urun
        ? apiYaz<Urun>("PATCH", `/urun/${urun.id}`, govde(kaydedilecekBarkodlar))
        : apiYaz<Urun>("POST", "/urun", govde(kaydedilecekBarkodlar));
    },
    onSuccess: kaydedildi,
  });

  const barkodlariDegistir = (yeni: string[]) => {
    setBarkodlar(yeni);
    setDevredilecek((d) => d.filter((b) => yeni.includes(b)));
  };

  // HID barkod okuyucu, odak bir metin alanında DEĞİLKEN de barkodu listeye
  // ekler; "Barkod ekle" alanına tıklamak gerekmez. Liste yalnızca formun
  // state'idir: kayıt yine "Kaydet" ile olur, yanlış okuma × ile çıkarılır.
  const barkodlarRef = useRef(barkodlar);
  barkodlarRef.current = barkodlar;
  useEffect(
    () =>
      hidOkuyucuDinle((okuma) => {
        if (barkodlarRef.current.includes(okuma)) return;
        barkodlariDegistir([...barkodlarRef.current, okuma]);
      }),
    // Abonelik bir kez kurulur; güncel liste ref üzerinden okunur.
    [],
  );

  const barkodKontrolu = useBarkodSahipleri(barkodlar, urun?.id ?? null, barkodTaslagi);
  const barkodSahipleri = barkodKontrolu.data ?? [];
  const onaysizCakisma = barkodSahipleri.filter(
    (s) => !devredilecek.includes(s.barkod),
  );
  // Ön kontrolü atlatan durum (yarış, süzgeç dışı kayıt): sunucunun 409'u.
  const sunucuCakismasi =
    kaydet.error instanceof ApiHatasi &&
    kaydet.error.kod === HataKodu.BARKOD_KULLANIMDA
      ? kaydet.error.detay
      : undefined;

  const adHatasi =
    gonderildi && !ad.trim() ? "Ürün adı zorunludur." : undefined;
  const satisHatasi =
    gonderildi && !paraGecerliMi(satisFiyat)
      ? "Satış fiyatını 12,50 biçiminde girin."
      : undefined;
  const alisHatasi =
    alisFiyat.trim() && !paraGecerliMi(alisFiyat)
      ? "Alış fiyatını 12,50 biçiminde girin."
      : undefined;
  const kdvHatasi =
    kdv.trim() && !/^\d{1,3}([.,]\d{1,2})?$/.test(kdv.trim())
      ? "KDV oranını 10 veya 10,00 biçiminde girin."
      : undefined;
  const kritikHatasi =
    kritik.trim() && !miktarGecerliMi(kritik)
      ? "Kritik seviyeyi 5 veya 5,500 biçiminde girin."
      : undefined;
  const barkodHatasi =
    barkodTaslagi.trim().length > 64 ||
    barkodlar.some((b) => !b.trim() || b.length > 64)
      ? "Barkod 1–64 karakter olmalıdır."
      : undefined;

  return (
    <Kart baslik={urun ? `Ürün düzenle: ${urun.ad}` : "Yeni ürün"}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (
            !ad.trim() ||
            !paraGecerliMi(satisFiyat) ||
            alisHatasi ||
            kdvHatasi ||
            kritikHatasi ||
            barkodHatasi ||
            kaydet.isPending ||
            // Çakışan barkod onaylanmadan kaydetme: sunucu da reddederdi ama
            // kullanıcı hatayı alanın yanında, kararı verdiği yerde görsün.
            onaysizCakisma.length > 0
          )
            return;
          kaydet.mutate();
        }}
      >
        <div className="flex flex-col gap-3">
          <EtiketDuzenleyici
            etiket="Barkod ekle"
            autoFocus={!urun}
            ipucu="Önce barkodu okutun veya yazın; kayıtlıysa ürün adı gösterilir. Barkodsuz ürün için boş bırakabilirsiniz. Birden fazla barkod için Ekle veya Enter kullanın."
            degerler={barkodlar}
            degistir={barkodlariDegistir}
            tekil="barkod"
            taslak={barkodTaslagi}
            taslakDegistir={(yeni) => {
              setBarkodTaslagi(yeni);
              if (kaydet.isError) kaydet.reset();
              setDevredilecek((d) => d.filter((b) => barkodlar.includes(b)));
            }}
            {...(barkodHatasi ? { hata: barkodHatasi } : {})}
          />
          {barkodKontrolu.isFetching && (
            <p role="status" className="m-0 text-sm text-murekkep-2">
              Barkod kontrol ediliyor…
            </p>
          )}
          {barkodKontrolu.isError && (
            <Uyari tur="uyari" baslik="Barkod kontrol edilemedi">
              Bağlantıyı kontrol edip tekrar deneyin.
              <Buton type="button" tur="sessiz" onClick={() => void barkodKontrolu.refetch()}>
                Tekrar dene
              </Buton>
            </Uyari>
          )}
          {sunucuCakismasi && (
            <BarkodCakismaUyarisi
              sahip={{
                barkod: sunucuCakismasi["barkod"] ?? "",
                urun_id: sunucuCakismasi["urun_id"] ?? "",
                urun_ad: sunucuCakismasi["urun_ad"] ?? "",
              }}
              devredilecek={devredilecek.includes(
                sunucuCakismasi["barkod"] ?? "",
              )}
              devret={() =>
                setDevredilecek((d) => [...d, sunucuCakismasi["barkod"] ?? ""])
              }
              geriAl={() =>
                setDevredilecek((d) =>
                  d.filter((b) => b !== sunucuCakismasi["barkod"]),
                )
              }
              barkodAra={barkodAra}
            />
          )}
          {barkodSahipleri.map((sahip) => (
            <BarkodCakismaUyarisi
              key={sahip.barkod}
              sahip={sahip}
              devredilecek={devredilecek.includes(sahip.barkod)}
              devret={() =>
                setDevredilecek((d) => [...d, sahip.barkod])
              }
              geriAl={() =>
                setDevredilecek((d) => d.filter((b) => b !== sahip.barkod))
              }
              barkodAra={barkodAra}
            />
          ))}
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Alan
            etiket="Ürün adı"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            {...(adHatasi ? { hata: adHatasi } : {})}
          />
          <Secim
            etiket="Kategori"
            value={kategoriId}
            onChange={(e) => setKategoriId(e.target.value)}
            secenekler={[
              { deger: "", metin: "Kategorisiz" },
              ...(kategoriler.data?.kategoriler ?? []).map((k) => ({
                deger: k.id,
                metin: k.ad,
              })),
            ]}
          />
          <Alan
            etiket="Birim"
            value={birim}
            onChange={(e) => setBirim(e.target.value)}
            ipucu="adet, kg, porsiyon..."
          />
          <Alan
            etiket="Satış fiyatı"
            value={satisFiyat}
            onChange={(e) => setSatisFiyat(e.target.value)}
            inputMode="decimal"
            {...(satisHatasi ? { hata: satisHatasi } : {})}
          />
          <Alan
            etiket="Alış fiyatı"
            value={alisFiyat}
            onChange={(e) => setAlisFiyat(e.target.value)}
            inputMode="decimal"
            ipucu="Kâr-zarar raporunun kaynağı"
            {...(alisHatasi ? { hata: alisHatasi } : {})}
          />
          <Alan
            etiket="KDV oranı (%)"
            value={kdv}
            onChange={(e) => setKdv(e.target.value)}
            inputMode="decimal"
            {...(kdvHatasi ? { hata: kdvHatasi } : {})}
          />
          <Alan
            etiket="Kritik stok seviyesi"
            value={kritik}
            onChange={(e) => setKritik(e.target.value)}
            inputMode="decimal"
            ipucu="Bu seviyenin altına düşünce düşük stok listesinde çıkar."
            {...(kritikHatasi ? { hata: kritikHatasi } : {})}
          />
          <Alan
            etiket="Hızlı buton sırası"
            value={hizliSira}
            onChange={(e) => setHizliSira(e.target.value)}
            inputMode="numeric"
            ipucu="1–99. Boş ise kasada hızlı butonda görünmez."
          />
          <Alan
            etiket="Görsel adresi"
            value={gorsel}
            onChange={(e) => setGorsel(e.target.value)}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <EtiketDuzenleyici
            etiket="Alerjen ekle"
            ipucu="Örnek: fıstık, süt, gluten. Veli alerjen yasağı bu etiketlerle çalışır."
            degerler={alerjenler}
            degistir={setAlerjenler}
            tekil="alerjen"
          />
        </div>

        <div className="flex flex-col gap-3 rounded-md border border-cetvel bg-kagit-2 p-3">
          <OnayKutusu
            etiket="Son kullanma tarihi takibi açık"
            aciklama="Açıkken mal girişinde SKT zorunlu olur ve tarihi yaklaşan partiler proaktif uyarı üretir."
            isaretli={sktTakibi}
            degistir={setSktTakibi}
          />
          {sktTakibi && (
            <div className="grid gap-3 md:grid-cols-2">
              <Alan
                etiket="Raf ömrü (gün)"
                value={rafOmru}
                onChange={(e) => setRafOmru(e.target.value)}
                inputMode="numeric"
              />
              <Alan
                etiket="İade eşiği (gün)"
                value={iadeEsigi}
                onChange={(e) => setIadeEsigi(e.target.value)}
                inputMode="numeric"
                ipucu="Tarihine bu kadar gün kalanlar iade/karantina için uyarılır."
              />
            </div>
          )}
        </div>

        <OnayKutusu
          etiket="Özel Sipariş Edilir"
          aciklama="Bu ürünün bugünkü satışları kasada özel sipariş filtresinde ve adet özetinde gösterilir."
          isaretli={ozelSiparis}
          degistir={setOzelSiparis}
        />

        {urun && (
          <p className="text-sm text-murekkep-2">
            Durum: {urun.aktif ? "Aktif" : "Pasif"}. Durumu değiştirmek için
            ürün listesindeki “Pasife al” veya “Aktifleştir” düğmesini kullanın.
          </p>
        )}

        {!sunucuCakismasi && <IslemDurumu hata={kaydet.error} />}

        <div className="flex flex-wrap gap-2">
          <Buton
            type="submit"
            tur="birincil"
            yukleniyor={kaydet.isPending}
            disabled={onaysizCakisma.length > 0}
          >
            {urun ? "Değişiklikleri kaydet" : "Ürünü kaydet"}
          </Buton>
          <Buton type="button" tur="ikincil" onClick={kapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// KK-505 · Toplu fiyat
// ---------------------------------------------------------------------------

interface TopluFiyatSonucu {
  degisen_sayisi: number;
  degisenler: { urun_id: string; eski: string; yeni: string }[];
}

function TopluFiyat({
  secilenler,
  secimTemizle,
}: {
  secilenler: string[];
  secimTemizle: () => void;
}) {
  const kategoriler = useKategoriler();
  const istemci = useQueryClient();

  const [hedef, setHedef] = useState<"secili" | "kategori">(
    secilenler.length > 0 ? "secili" : "kategori",
  );
  const [kategoriId, setKategoriId] = useState("");
  const [tip, setTip] = useState<"yuzde" | "tutar">("yuzde");
  const [yon, setYon] = useState<"zam" | "indirim">("zam");
  const [deger, setDeger] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const uygula = useMutation({
    mutationFn: () =>
      apiYaz<TopluFiyatSonucu>("POST", "/urun/toplu-fiyat", {
        ...(hedef === "kategori"
          ? { kategori_id: kategoriId }
          : { urun_idler: secilenler }),
        tip,
        deger: `${yon === "indirim" ? "-" : ""}${paraNormalize(deger)}`,
        ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
      }),
    onSuccess: () => {
      void istemci.invalidateQueries({ queryKey: ["urun"] });
      void istemci.invalidateQueries({ queryKey: ["audit-fiyat"] });
    },
  });

  const degerHatasi =
    gonderildi && !paraGecerliMi(deger)
      ? "Değeri 10 veya 1,50 biçiminde girin (işaret aşağıdaki seçimle verilir)."
      : undefined;
  const hedefHatasi =
    gonderildi && hedef === "kategori" && !kategoriId
      ? "Bir kategori seçin."
      : gonderildi && hedef === "secili" && secilenler.length === 0
        ? "Ürün listesi sekmesinden en az bir ürün seçin."
        : undefined;

  return (
    <Kart baslik="Toplu fiyat güncelleme">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!paraGecerliMi(deger)) return;
          if (hedef === "kategori" && !kategoriId) return;
          if (hedef === "secili" && secilenler.length === 0) return;
          uygula.mutate();
        }}
      >
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 p-0 text-sm font-semibold text-murekkep-2">
            Hangi ürünler
          </legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="toplu-hedef"
              checked={hedef === "secili"}
              onChange={() => setHedef("secili")}
              className="h-4 w-4 accent-simit"
            />
            Seçili ürünler (
            <span className="rakam font-mono">{secilenler.length}</span>)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="toplu-hedef"
              checked={hedef === "kategori"}
              onChange={() => setHedef("kategori")}
              className="h-4 w-4 accent-simit"
            />
            Bir kategorinin tamamı
          </label>
          {hedefHatasi && (
            <p className="m-0 text-xs text-hata" role="alert">
              {hedefHatasi}
            </p>
          )}
        </fieldset>

        <div className="grid gap-3 md:grid-cols-4">
          {hedef === "kategori" && (
            <Secim
              etiket="Kategori"
              value={kategoriId}
              onChange={(e) => setKategoriId(e.target.value)}
              secenekler={[
                { deger: "", metin: "Seçiniz" },
                ...(kategoriler.data?.kategoriler ?? []).map((k) => ({
                  deger: k.id,
                  metin: `${k.ad} (${k.urun_sayisi})`,
                })),
              ]}
            />
          )}
          <Secim
            etiket="Değişim tipi"
            value={tip}
            onChange={(e) =>
              setTip(e.target.value === "tutar" ? "tutar" : "yuzde")
            }
            secenekler={[
              { deger: "yuzde", metin: "Yüzde (%)" },
              { deger: "tutar", metin: "Tutar (₺)" },
            ]}
          />
          <Secim
            etiket="Yön"
            value={yon}
            onChange={(e) =>
              setYon(e.target.value === "indirim" ? "indirim" : "zam")
            }
            secenekler={[
              { deger: "zam", metin: "Zam (+)" },
              { deger: "indirim", metin: "İndirim (−)" },
            ]}
          />
          <Alan
            etiket={tip === "yuzde" ? "Yüzde değeri" : "Tutar değeri"}
            value={deger}
            onChange={(e) => setDeger(e.target.value)}
            inputMode="decimal"
            {...(degerHatasi ? { hata: degerHatasi } : {})}
          />
        </div>

        <MetinAlani
          etiket="Açıklama (denetim kaydına geçer)"
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          rows={2}
          ipucu="Örnek: Tedarikçi zammı sonrası Mart ayı güncellemesi."
        />

        <Uyari tur="uyari" baslik="Bu işlem geri alınamaz">
          <p className="m-0 mb-1">
            Yeni fiyatlar okulun tüm kantinlerinde geçerli olur.
          </p>
          <p className="m-0">
            Değişiklik <strong>tek işlemde</strong> uygulanır. Herhangi bir
            ürünün yeni fiyatı negatif çıkarsa işlemin tamamı reddedilir —
            sessizce sıfıra kırpılmaz.
          </p>
          <p className="m-0 mt-1">
            Her satır için fiyat geçmişi yazılır ve işlem tek bir hassas denetim
            kaydıyla özetlenir. Geçmiş satışlar etkilenmez.
          </p>
        </Uyari>

        <IslemDurumu hata={uygula.error} />

        {uygula.data && (
          <div className="rounded-lg border border-onay-cizgi bg-onay-yumusak p-3">
            <p className="m-0 font-semibold text-murekkep">
              <span className="rakam font-mono">
                {uygula.data.degisen_sayisi}
              </span>{" "}
              ürünün fiyatı güncellendi.
            </p>
            {uygula.data.degisenler.length > 0 && (
              <div className="mt-2">
                <Tablo>
                  <TabloBaslik>
                    <th scope="col">Ürün</th>
                    <th scope="col" className="!text-right">
                      Eski
                    </th>
                    <th scope="col" className="!text-right">
                      Yeni
                    </th>
                  </TabloBaslik>
                  <TabloGovde>
                    {uygula.data.degisenler.map((d) => (
                      <tr key={d.urun_id}>
                        <td className="rakam font-mono text-xs">{d.urun_id}</td>
                        <td className="text-right">
                          <Tutar deger={d.eski} yonsuz boy="sm" />
                        </td>
                        <td className="text-right">
                          <Tutar deger={d.yeni} yonsuz boy="sm" />
                        </td>
                      </tr>
                    ))}
                  </TabloGovde>
                </Tablo>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={uygula.isPending}>
            Toplu fiyatı uygula
          </Buton>
          {secilenler.length > 0 && (
            <Buton type="button" tur="sessiz" onClick={secimTemizle}>
              Seçimi temizle
            </Buton>
          )}
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// Fiyat geçmişi — audit log (KK-505 / KK-1505)
// ---------------------------------------------------------------------------

interface AuditKaydi {
  id: string;
  aktor_ad: string | null;
  eylem: string;
  varlik_id: string | null;
  onceki: { satis_fiyat?: string } | null;
  sonraki: {
    satis_fiyat?: string;
    toplu?: boolean;
    tip?: string;
    deger?: string;
    degisen_sayisi?: number;
    nokta_id?: string | null;
  } | null;
  aciklama: string | null;
  olusma_zamani: string;
}

function FiyatGecmisi() {
  const [sayfa, setSayfa] = useState(1);
  const sorgu = useQuery({
    queryKey: ["audit-fiyat", sayfa],
    queryFn: () =>
      apiOku<{ kayitlar: AuditKaydi[]; toplam: number; sayfa_boyu: number }>(
        "/audit",
        { eylem: "urun.fiyat_degisim", sayfa },
      ),
  });

  const kayitlar = sorgu.data?.kayitlar ?? [];

  return (
    <Kart
      baslik="Fiyat geçmişi"
      ustBilgi={
        <span className="text-sm text-murekkep-3">
          Denetim kaydından okunur (urun.fiyat_degisim)
        </span>
      }
    >
      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={kayitlar.length === 0}
        bosBaslik="Fiyat değişikliği yok"
        bosAciklama="Henüz hiçbir ürünün satış fiyatı değiştirilmemiş."
        yenile={() => void sorgu.refetch()}
      >
        <Tablo>
          <TabloBaslik>
            <th scope="col">Zaman</th>
            <th scope="col">Değişiklik</th>
            <th scope="col">Yapan</th>
            <th scope="col">Açıklama</th>
          </TabloBaslik>
          <TabloGovde>
            {kayitlar.map((k) => (
              <tr key={k.id}>
                <td className="whitespace-nowrap text-xs text-murekkep-3">
                  {tarihSaatGoster(k.olusma_zamani)}
                </td>
                <td>
                  {k.sonraki?.toplu ? (
                    <span>
                      Toplu: {k.sonraki.tip === "yuzde" ? "%" : "₺"}{" "}
                      <span className="rakam font-mono">
                        {k.sonraki.deger ?? "—"}
                      </span>{" "}
                      ·{" "}
                      <span className="rakam font-mono">
                        {k.sonraki.degisen_sayisi ?? 0}
                      </span>{" "}
                      ürün
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-murekkep-3">
                        Ürün #{k.varlik_id ?? "—"}
                      </span>
                      {k.onceki?.satis_fiyat && (
                        <Tutar
                          deger={k.onceki.satis_fiyat}
                          yonsuz
                          boy="sm"
                          etiket="eski fiyat"
                        />
                      )}
                      <span aria-hidden="true">→</span>
                      {k.sonraki?.satis_fiyat && (
                        <Tutar
                          deger={k.sonraki.satis_fiyat}
                          yonsuz
                          boy="sm"
                          etiket="yeni fiyat"
                        />
                      )}
                    </span>
                  )}
                </td>
                <td className="text-sm text-murekkep-2">{k.aktor_ad ?? "—"}</td>
                <td className="text-sm text-murekkep-2">{k.aciklama ?? "—"}</td>
              </tr>
            ))}
          </TabloGovde>
        </Tablo>
        <div className="flex items-center justify-between gap-3 border-t border-cetvel px-3 py-2 text-sm text-murekkep-2">
          <span className="rakam font-mono">
            Sayfa {sayfa} / toplam {sorgu.data?.toplam ?? 0} kayıt
          </span>
          <span className="flex gap-2">
            <Buton
              tur="ikincil"
              disabled={sayfa <= 1}
              onClick={() => setSayfa((s) => Math.max(1, s - 1))}
            >
              Önceki
            </Buton>
            <Buton
              tur="ikincil"
              disabled={kayitlar.length < (sorgu.data?.sayfa_boyu ?? 50)}
              onClick={() => setSayfa((s) => s + 1)}
            >
              Sonraki
            </Buton>
          </span>
        </div>
      </SorguDurumu>
    </Kart>
  );
}
